import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import { Heart, Menu, Search, Tv, X, User, LogOut } from "lucide-react";
import { useSaved } from "../hooks/useSaved";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/explore", label: "Explore" },
    { to: "/companies", label: "Companies" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const { saved } = useSaved();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    if (loc.pathname.startsWith("/tv") || loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/agent") || loc.pathname.startsWith("/login") || loc.pathname.startsWith("/presentations") || loc.pathname.startsWith("/forgot-password") || loc.pathname.startsWith("/reset-password")) return null;

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "glass border-b border-white/10" : "bg-transparent"
            }`}
        >
            <div className="container-luxury flex items-center justify-between h-20">
                <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
                    <span className="font-serif text-2xl tracking-tight text-white">
                        Tile<span className="text-gold">Sphere</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm tracking-[0.18em] uppercase transition-colors duration-300 ${
                                    isActive ? "text-gold" : "text-white/80 hover:text-gold"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2 md:gap-3">
                    <Link to="/explore" data-testid="nav-search-btn" className="p-2.5 text-white/80 hover:text-gold transition-colors">
                        <Search size={18} strokeWidth={1.4} />
                    </Link>
                    <Link to="/saved" data-testid="nav-saved-btn" className="relative p-2.5 text-white/80 hover:text-gold transition-colors">
                        <Heart size={18} strokeWidth={1.4} />
                        {saved.length > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-gold text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {saved.length}
                            </span>
                        )}
                    </Link>
                    <Link to="/tv" data-testid="nav-tv-btn" className="hidden md:inline-flex p-2.5 text-white/80 hover:text-gold transition-colors" title="TV Showroom Mode">
                        <Tv size={18} strokeWidth={1.4} />
                    </Link>

                    {user && user !== false ? (
                        <div className="hidden md:flex items-center gap-2 ml-2 pl-3 border-l border-white/10">
                            <Link
                                to={user.role === "super_admin" || user.role === "company_admin" ? "/admin" : "/agent"}
                                data-testid="nav-dashboard-btn"
                                className="text-xs tracking-[0.2em] uppercase text-gold hover:text-gold-soft"
                            >
                                {user.role === "super_admin" ? "Admin" : user.role === "company_admin" ? "Company" : "My Portal"}
                            </Link>
                            <button onClick={logout} data-testid="nav-logout-btn" className="p-2 text-white/70 hover:text-gold">
                                <LogOut size={16} strokeWidth={1.4} />
                            </button>
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            data-testid="nav-login-btn"
                            className="hidden md:inline-flex items-center gap-1.5 ml-2 pl-3 border-l border-white/10 text-xs tracking-[0.2em] uppercase text-white/80 hover:text-gold"
                        >
                            <User size={14} strokeWidth={1.4} /> Sign In
                        </Link>
                    )}

                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden p-2 text-white"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {open && (
                <div data-testid="mobile-nav-panel" className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
                    <div className="container-luxury py-6 flex flex-col gap-4">
                        {NAV.map((item) => (
                            <NavLink key={item.to} to={item.to} className="text-white/90 text-base tracking-wide py-2 border-b border-white/5">
                                {item.label}
                            </NavLink>
                        ))}
                        {user && user !== false ? (
                            <>
                                <Link to={user.role === "sales_agent" ? "/agent" : "/admin"} className="text-gold tracking-wide py-2">
                                    Dashboard
                                </Link>
                                <button onClick={logout} className="text-left text-white/80 py-2">Logout</button>
                            </>
                        ) : (
                            <Link to="/login" className="text-gold tracking-wide py-2">Sign In</Link>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
